import { useState } from "react";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
} from "@mui/x-data-grid-premium";
import { useTenantTranslation } from "hooks/formatters";

export const SplitFixedMixButton = ({
  gradeNames,
  disabled,
  onSplit,
  ...actionProps
}: Omit<
  Extract<GridActionsCellItemProps, { showInMenu?: false }>,
  "icon" | "label" | "onClick"
> & {
  gradeNames: string[];
  disabled: boolean;
  onSplit: (gradeNames: string[]) => void;
}) => {
  const { t } = useTenantTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const validSelection = selected.filter((name) => gradeNames.includes(name));
  return (
    <>
      <GridActionsCellItem
        {...actionProps}
        icon={<CallSplitRoundedIcon sx={{ transform: "rotate(90deg)" }} />}
        label={t("splitMix")}
        disabled={disabled}
        onClick={(event) => {
          setSelected([]);
          setAnchor(event.currentTarget);
        }}
      />
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack gap={2} sx={{ p: 2, minWidth: 260 }}>
          <Typography variant="h3">{t("splitMix")}</Typography>
          <Typography>{t("selectGradesForSeparateMix")}</Typography>
          <FormGroup>
            {gradeNames.map((name) => (
              <FormControlLabel
                key={name}
                label={name}
                control={
                  <Checkbox
                    checked={validSelection.includes(name)}
                    onChange={(_, checked) => {
                      setSelected((previous) =>
                        checked
                          ? [...previous, name]
                          : previous.filter((grade) => grade !== name)
                      );
                    }}
                  />
                }
              />
            ))}
          </FormGroup>
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button
              onClick={() => {
                setAnchor(null);
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              disabled={
                disabled ||
                validSelection.length === 0 ||
                validSelection.length === gradeNames.length
              }
              onClick={() => {
                onSplit(validSelection);
                setAnchor(null);
              }}
            >
              {t("splitMix")}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
